<script setup lang="ts">
import type { SessionWithToken } from "~/types/api/auth/login"
import SvgoPikaLogout from "~ui/assets/icons/pika/logout.svg"
import StatusDot from "~ui/components/StatusDot.vue"

const { session, token, workspaceId, getSession } = useAuth()
const { isOpen, toggle } = useSidebarNavigation()
const { showKickoffPlan, showOnboarding } = useWorkspaceOnboarding()
const workspaceSearch = ref("")

const availableWorkspaces = computed(() => {
  return session.value?.available_workspaces ?? []
})

const filteredWorkspaces = computed(() => {
  if (workspaceSearch.value.trim() == "") {
    return availableWorkspaces.value
  }

  return availableWorkspaces.value.filter((workspace) =>
    workspace.name.toLowerCase().includes(workspaceSearch.value.toLowerCase()),
  )
})

const { $api } = useNuxtApp()

type WorkspaceSwitchSession = Pick<
  SessionWithToken,
  "user" | "workspace" | "token"
>
const { mutate: switchWorkspace, loading: switchWorkspaceLoading } =
  useMutation({
    fetchFunction: async (workspaceId: string) => {
      const result = await $api<WorkspaceSwitchSession>(
        "/admin/v1/workspace/switch",
        {
          method: "post",
          body: {
            workspace_id: workspaceId,
          },
        },
      )
      return result
    },
    onSuccess: async (result) => {
      token.value = result.token
      workspaceId.value = result.workspace

      await getSession()
      await refreshNuxtData()
      await navigateTo({ name: "dashboard" })
    },
  })

const selectedWorkspaceId = computed({
  get: () => currentWorkspace.value?.id,
  set: async (value: string) => {
    if (!value || value == session.value?.workspace) {
      return
    }
    await switchWorkspace(value)
  },
})

const currentWorkspace = computed(() => {
  return availableWorkspaces.value.find((workspace) => workspace.is_current)
})
</script>
<template>
  <div
    class="sticky inset-y-0 z-20 h-screen max-lg:hidden"
    :class="{ 'w-64': isOpen, 'w-12': !isOpen }"
  >
    <!-- Amplitude uses this state attribute to control guide visibility. -->
    <nav
      class="@container/navigation flex h-full flex-col"
      :data-nav-opened="isOpen"
    >
      <div
        class="flex justify-between border-b border-zinc-950/5 p-4"
        :class="{ 'flex-row': isOpen, 'flex-col items-center gap-4': !isOpen }"
      >
        <NuxtLink
          v-if="isOpen || !session?.company_logo_url"
          :to="{ name: 'dashboard' }"
          class="relative"
        >
          <CompanyLogo
            v-if="session?.company_logo_url && isOpen"
            :url="session.company_logo_url"
          />
          <LogoSvg v-else class="size-8" />
        </NuxtLink>
        <button class="cursor-pointer" @click="toggle()">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            height="1em"
            width="1em"
            aria-hidden="true"
            data-slot="icon"
            class="group size-5 text-zinc-600"
            viewBox="0 0 18 18"
          >
            <g fill="currentColor">
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M6.27218 1.90039L11.7247 1.90039C12.5425 1.90039 13.1929 1.90038 13.7177 1.94326C14.2551 1.98716 14.7133 2.079 15.1328 2.29277C15.8102 2.63791 16.3609 3.18864 16.7061 3.86602C16.9198 4.28557 17.0117 4.74378 17.0556 5.28114C17.0984 5.80589 17.0984 6.45632 17.0984 7.27415V10.7266C17.0984 11.5445 17.0984 12.1949 17.0556 12.7196C17.0117 13.257 16.9198 13.7152 16.7061 14.1348C16.3609 14.8121 15.8102 15.3629 15.1328 15.708C14.7133 15.9218 14.2551 16.0136 13.7177 16.0575C13.1929 16.1004 12.5425 16.1004 11.7247 16.1004H6.2722C5.45436 16.1004 4.80394 16.1004 4.27918 16.0575C3.74182 16.0136 3.28362 15.9218 2.86407 15.708C2.18669 15.3629 1.63596 14.8121 1.29081 14.1348C1.07704 13.7152 0.985207 13.257 0.941303 12.7196C0.898429 12.1949 0.898433 11.5445 0.898438 10.7266L0.898438 7.27414C0.898433 6.45631 0.898429 5.80589 0.941303 5.28114C0.985207 4.74378 1.07704 4.28557 1.29081 3.86602C1.63596 3.18864 2.18669 2.63791 2.86407 2.29277C3.28362 2.079 3.74182 1.98716 4.27918 1.94326C4.80394 1.90038 5.45436 1.90039 6.27218 1.90039ZM4.3769 3.13927C3.91375 3.17711 3.63105 3.24877 3.40886 3.36198C2.95727 3.59207 2.59012 3.95922 2.36002 4.41081C2.24681 4.633 2.17516 4.9157 2.13732 5.37886C2.0989 5.84901 2.09844 6.45041 2.09844 7.30039L2.09844 10.7004C2.09844 11.5504 2.0989 12.1518 2.13732 12.6219C2.17516 13.0851 2.24681 13.3678 2.36002 13.59C2.59012 14.0416 2.95727 14.4087 3.40886 14.6388C3.63105 14.752 3.91375 14.8237 4.3769 14.8615C4.84706 14.8999 5.44846 14.9004 6.29844 14.9004L11.6984 14.9004C12.5484 14.9004 13.1498 14.8999 13.62 14.8615C14.0831 14.8237 14.3658 14.752 14.588 14.6388C15.0396 14.4087 15.4068 14.0416 15.6369 13.59C15.7501 13.3678 15.8217 13.0851 15.8596 12.6219C15.898 12.1518 15.8984 11.5504 15.8984 10.7004L15.8984 7.30039C15.8984 6.45041 15.898 5.84901 15.8596 5.37886C15.8217 4.9157 15.7501 4.633 15.6369 4.41081C15.4068 3.95922 15.0396 3.59207 14.588 3.36198C14.3658 3.24877 14.0831 3.17711 13.62 3.13927C13.1498 3.10086 12.5484 3.10039 11.6984 3.10039L6.29844 3.10039C5.44846 3.10039 4.84706 3.10086 4.3769 3.13927Z"
                fill="currentColor"
              />
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M7.19531 15.2246L7.19531 2.72461H8.39531L8.39531 15.2246H7.19531Z"
                fill="currentColor"
                :class="{
                  'translate-x-0 scale-100 transition duration-150 ease-in group-hover:-translate-x-1.5 group-hover:scale-[0.9] group-hover:opacity-0':
                    isOpen,
                  '-translate-x-1.5 scale-[0.9] opacity-0 transition duration-150 ease-in group-hover:translate-x-0 group-hover:scale-100 group-hover:opacity-100':
                    !isOpen,
                }"
              />
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M3.375 5.42617C3.375 5.0948 3.64363 4.82617 3.975 4.82617H5.325C5.65637 4.82617 5.925 5.0948 5.925 5.42617C5.925 5.75754 5.65637 6.02617 5.325 6.02617H3.975C3.64363 6.02617 3.375 5.75754 3.375 5.42617Z"
                fill="currentColor"
                :class="{
                  'translate-x-0 transition duration-150 ease-in group-hover:-translate-x-1 group-hover:opacity-0':
                    isOpen,
                  '-translate-x-1.5 scale-[0.9] opacity-0 transition duration-150 ease-in group-hover:translate-x-0 group-hover:scale-100 group-hover:opacity-100':
                    !isOpen,
                }"
              />
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M3.375 7.67422C3.375 7.34285 3.64363 7.07422 3.975 7.07422H5.325C5.65637 7.07422 5.925 7.34285 5.925 7.67422C5.925 8.00559 5.65637 8.27422 5.325 8.27422H3.975C3.64363 8.27422 3.375 8.00559 3.375 7.67422Z"
                fill="currentColor"
                :class="{
                  'translate-x-0 transition duration-150 ease-in group-hover:-translate-x-1 group-hover:opacity-0':
                    isOpen,
                  '-translate-x-1 opacity-0 transition duration-150 ease-in group-hover:translate-x-0 group-hover:opacity-100':
                    !isOpen,
                }"
              />
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M5.82739 11.2247C5.59308 11.459 5.21318 11.459 4.97886 11.2247L3.17886 9.42466C2.94455 9.19034 2.94455 8.81044 3.17886 8.57613L4.97886 6.77613C5.21317 6.54181 5.59307 6.54181 5.82739 6.77613C6.0617 7.01044 6.0617 7.39034 5.82739 7.62465L5.05165 8.40039H8.10312C8.4345 8.40039 8.70312 8.66902 8.70312 9.00039C8.70312 9.33176 8.4345 9.60039 8.10312 9.60039H5.05166L5.82739 10.3761C6.0617 10.6104 6.0617 10.9903 5.82739 11.2247Z"
                fill="currentColor"
                :class="{
                  'translate-x-1.5 opacity-0 transition duration-200 ease-in group-hover:translate-x-0 group-hover:opacity-100':
                    isOpen,
                  'origin-center -translate-x-1.5 scale-x-[-1] transition duration-150 ease-in group-hover:translate-x-0':
                    !isOpen,
                }"
              />
            </g>
          </svg>
        </button>
      </div>
      <div
        class="flex flex-1 flex-col overflow-y-auto"
        :class="{ 'p-3': isOpen, 'mt-2 items-center': !isOpen }"
      >
        <SidebarNavigationItems />
        <SidebarLocaleSwitcher v-if="isOpen" class="w-full" />
        <div
          v-if="showOnboarding || showKickoffPlan"
          class="mt-3"
          :class="{ 'mb-2': !isOpen }"
        >
          <WorkspaceOnboardingProgressBadge :collapsed="!isOpen" />
        </div>
      </div>
      <div
        class="flex flex-col gap-2 border-t border-zinc-950/5"
        :class="{ 'items-center px-1 pt-1 pb-2': !isOpen, 'p-2': isOpen }"
      >
        <div>
          <DropdownMenu v-if="session" @close="workspaceSearch = ''">
            <DropdownTrigger
              class="relative flex w-full cursor-pointer items-center justify-between rounded-lg border border-zinc-200 bg-white transition-colors hover:bg-zinc-50"
              data-testid="user_info_button"
              :class="{ 'p-0.75': !isOpen, 'p-2': isOpen }"
            >
              <div class="flex min-w-0 items-center">
                <Avatar :name="session.user.first_name" />
                <div
                  class="ml-2 hidden w-full min-w-0 flex-col text-left text-base/6 font-medium text-zinc-950 @[12rem]/navigation:flex"
                >
                  <p
                    class="block truncate text-[13px]/4 font-medium text-zinc-950"
                  >
                    {{ session.user.first_name }}
                  </p>
                  <p class="block truncate text-xs/4 font-normal text-zinc-500">
                    {{ currentWorkspace?.name }}
                  </p>
                </div>
              </div>
              <div v-if="isOpen" class="ml-2">
                <SvgoPikaChevronSortVertical class="size-4.5 text-zinc-400" />
              </div>
            </DropdownTrigger>
            <DropdownContent align="start" @close-auto-focus.prevent>
              <div v-if="availableWorkspaces.length > 1">
                <div
                  class="flex items-center gap-1.5 border-b border-zinc-100 p-2"
                >
                  <SvgoPikaSearch class="size-4 text-zinc-500" />
                  <input
                    v-model="workspaceSearch"
                    type="text"
                    :placeholder="$t('navigation.search_workspaces')"
                    class="w-full appearance-none border-0 bg-transparent p-0 text-sm text-zinc-800 shadow-none outline-hidden placeholder:text-zinc-500 focus:ring-0 focus-visible:outline-hidden"
                    @keydown.stop
                    @keyup.stop
                  />
                </div>

                <div
                  class="relative max-h-60 overflow-y-auto border-b border-zinc-200 py-0.5"
                >
                  <div
                    v-if="switchWorkspaceLoading"
                    class="absolute inset-0 z-10 flex h-full w-full cursor-default items-center justify-center bg-white/85"
                  >
                    <SvgoLoading class="size-4 animate-spin text-zinc-700" />
                  </div>

                  <div
                    v-if="filteredWorkspaces.length == 0"
                    class="w-full py-3 text-center"
                  >
                    <TypographyCaption class="px-2 py-2 text-zinc-500">
                      {{ $t("navigation.no_workspaces") }}
                    </TypographyCaption>
                  </div>

                  <template v-else>
                    <TypographyCaption class="px-2.25 pt-2 pb-1 font-medium">
                      {{ $t("navigation.your_workspaces") }}
                    </TypographyCaption>

                    <label
                      v-for="workspace in filteredWorkspaces"
                      :key="workspace.id"
                      class="mx-1 my-0.5 flex cursor-pointer items-center justify-between gap-4 rounded p-1.25 transition hover:bg-zinc-50 has-checked:bg-zinc-100"
                    >
                      <span
                        class="max-w-60 truncate text-sm font-normal text-zinc-600"
                      >
                        {{ workspace.name }}
                      </span>

                      <FormRadio
                        v-model="selectedWorkspaceId"
                        name="workspace"
                        :value="workspace.id"
                        :disabled="switchWorkspaceLoading"
                        class="size-3.5"
                      />
                    </label>
                  </template>
                </div>
              </div>
              <DropdownItem target="_blank" to="https://status.moxso.com">
                <StatusDot />System Status
              </DropdownItem>
              <DropdownItem to="/logout" data-testid="logout_button">
                <SvgoPikaLogout />
                {{ $t("navigation.sign_out") }}
              </DropdownItem>
            </DropdownContent>
          </DropdownMenu>
        </div>
      </div>
    </nav>
  </div>
</template>
